import { QueryHookOptions } from '@apollo/client';
import { useMemo } from 'react';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { TaskStatusEnum } from '../components/work/types/task-status-enum';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';
import { getTaskStatus } from '../globals';
import { parseSearchJobsVariables } from '../utils/data-utils';

/**
 * This comment can be removed after CPR-1879 is in PROD
 * The params that have been removed are:
 * in Job type: tenantId, WfDeletedOnUTC, startDate
 * in Task Type: assignedToUserId, dueDate, completedDate, entityId, name, taskTypeId, estimateMinutes, description
 */
export const SEARCH_JOBS_WITH_TASKS_QUERY = gql`
  query SearchJobsWithTasks(
    $clients: [String!] = []
    $myWork: Boolean = false
    $staffIds: [String!] = []
    $managerIds: [String!] = []
    $partnerIds: [String!] = []
    $statuses: [Float!] = []
    $dueDateBegin: DateTime = null
    $dueDateEnd: DateTime = null
    $dueDateOverdue: Boolean = false
    $startDateBegin: DateTime = null
    $startDateEnd: DateTime = null
    $closedDateBegin: DateTime = null
    $closedDateEnd: DateTime = null
    $completedDateBegin: DateTime = null
    $completedDateEnd: DateTime = null
    $familyGroupIds: [String!] = []
    $templateIds: [String!] = []
    $text: String = null
    $take: Float = null
    $statusIds: [String!] = []
    $assigneeIds: [String!] = []
  ) {
    searchJobs(
      clients: $clients
      myWork: $myWork
      staffIds: $staffIds
      managerIds: $managerIds
      partnerIds: $partnerIds
      statuses: $statuses
      dueDateBegin: $dueDateBegin
      dueDateEnd: $dueDateEnd
      dueDateOverdue: $dueDateOverdue
      startDateBegin: $startDateBegin
      startDateEnd: $startDateEnd
      closedDateBegin: $closedDateBegin
      closedDateEnd: $closedDateEnd
      completedDateBegin: $completedDateBegin
      completedDateEnd: $completedDateEnd
      familyGroupIds: $familyGroupIds
      templateIds: $templateIds
      text: $text
      take: $take
      statusIds: $statusIds
      assigneeIds: $assigneeIds
    ) {
      ...SearchJobsWithTasks_Job
    }
  }
` as import('../../__generated__/ts-gql/SearchJobsWithTasks').type;

export const SearchJobsWithTasksFragment = gql`
  fragment SearchJobsWithTasks_Job on Job {
    cloudId
    name
    clientId
    staffUserId
    managerUserId
    partnerUserId
    assigneeUserId
    jobStatusId
    dueDate
    completedDate
    closedDate
    budget
    signOffStatus
    tasks {
      cloudId
      status
      wfDeletedOnUTC
    }
  }
` as import('../../__generated__/ts-gql/SearchJobsWithTasks_Job').type;

export type SearchJobsWithTasksQueryVariables = OperationVariables<
  typeof SEARCH_JOBS_WITH_TASKS_QUERY
>;
export type SearchJobsWithTasksQueryResult = OperationData<
  typeof SEARCH_JOBS_WITH_TASKS_QUERY
>;
export type SearchJobsWithTasksType =
  SearchJobsWithTasksQueryResult['searchJobs'];

export type SearchJobsWithTasksResult = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useSearchJobsWithTasks = (
  options: QueryHookOptions<
    SearchJobsWithTasksQueryResult,
    SearchJobsWithTasksQueryVariables
  >
) => {
  const variables = options.variables as SearchJobsWithTasksQueryVariables;

  const { localDateToTenantToUtc, utcToTenantToLocalDate, loading } =
    useTenantTimezoneConversion();

  const res = useQuery(SEARCH_JOBS_WITH_TASKS_QUERY, {
    context: { service: 'core' },
    ...options,
    variables: parseSearchJobsVariables(variables, localDateToTenantToUtc),
    skip: options.skip || loading,
  });

  return useMemo(() => {
    if (loading) {
      return {
        loading: true,
        data: undefined,
        error: undefined,
        refetch: undefined,
      };
    }
    return {
      ...res,
      data: processData(res.data, utcToTenantToLocalDate),
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

export function processData(
  data: SearchJobsWithTasksQueryResult | undefined,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const raw = data?.searchJobs;

  const results =
    raw &&
    raw.map((job) => ({
      ...job,
      tasks:
        job.tasks
          ?.filter((task) => !task.wfDeletedOnUTC)
          .map((task) => {
            return {
              ...task,
              status: task.status
                ? getTaskStatus(task.status)
                : TaskStatusEnum.valuesEnum.NotStarted,
            };
          }) || [],
      dueDate: job.dueDate && dateConvert(job.dueDate),
      completedDate: job.completedDate && new Date(job.completedDate),
      closedDate: job.closedDate && new Date(job.closedDate),
    }));
  return results;
}
